import { defineStore } from "pinia";
import { objectToSnake } from "ts-case-convert";
import { ref, watch } from "vue";

import { createArgusEvent, createGtmEvent } from "@solvari/argus";
import { useIsVisible } from "@solvari/common-fe/composables";
import {
  injectI18nLocale,
  localeToCountry,
  localeToIso,
} from "@solvari/translations";

import type { SubmitLeadData } from "@/api/registration";

export const useGtmStore = defineStore("gtm", () => {
  const rootElement = ref<HTMLElement | null>(null);
  const isVisible = useIsVisible(rootElement, {
    rootMargin: "-100px",
  });

  const fieldFocusCounts = ref<Record<string, number>>({});
  function getFieldFocusAmount(fieldName: string) {
    return fieldFocusCounts.value[fieldName];
  }
  function setFieldFocused(fieldName: string) {
    if (fieldFocusCounts.value[fieldName]) {
      fieldFocusCounts.value[fieldName]++;
    } else {
      fieldFocusCounts.value[fieldName] = 1;
    }
  }

  const locale = injectI18nLocale();

  watch(isVisible, registrationViewEvent, { immediate: true });
  function registrationViewEvent() {
    // Triggered on first view of each step per registration
    if (!isVisible.value) {
      return;
    }

    return createGtmEvent("registration_view", {
      locale: localeToIso(locale.value),
    });
  }

  function generateCompanyEvent(data: SubmitLeadData & { customerId: number }) {
    return Promise.allSettled([
      createArgusEvent("form_submit", {
        id: data.customerId,
        type: "registration",
      }),
      createGtmEvent("generate_lead_company", {
        locale: localeToIso(locale.value),
        country: localeToCountry(locale.value),
        ...data,
      }),
    ]);
  }

  function registrationInteractionEvent({
    name,
    label,
  }: {
    label: string;
    name: string;
  }) {
    // Triggered every time a field is focused
    setFieldFocused(name);

    return createGtmEvent(
      "registration_interaction",
      objectToSnake({
        locale: localeToIso(locale.value),

        fieldLabel: label,
        fieldStructured: name,
        focusAmount: getFieldFocusAmount(name),
      }),
    );
  }

  function registrationErrorEvent({
    name,
    label,
    error,
  }: {
    error: string;
    label: string;
    name: string;
  }) {
    // Triggered whenever a field's error message changes
    return createGtmEvent(
      "registration_error",
      objectToSnake({
        locale: localeToIso(locale.value),

        fieldLabel: label,
        fieldStructured: name,
        errorMessage: error,
      }),
    );
  }

  return {
    rootElement,
    isVisible,
    registrationViewEvent,
    generateCompanyEvent,
    registrationInteractionEvent,
    registrationErrorEvent,
  };
});
